import React, { useEffect } from "react";

// ── Reusable number-search UI (country + auto-suggest + area-code search + results)
// Mirrors the Buy Numbers page look & feel so the claim-free-number flow matches it.
// Selection is controlled by the parent (single- or multi-select) via isSelected/onToggle.

const NSkel = ({ w, h = 13, rounded = false, style = {} }) => (
  <span
    style={{
      display: "block",
      width: w,
      height: h,
      borderRadius: rounded ? 999 : 6,
      background: "linear-gradient(90deg, #f0f0f0 25%, #e6e6e6 50%, #f0f0f0 75%)",
      backgroundSize: "600px 100%",
      animation: "np-shimmer 1.4s infinite linear",
      ...style,
    }}
  />
);

const NumberCardSkeleton = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "9px 12px",
      marginBottom: 5,
    }}
  >
    <div className="d-flex align-items-center gap-2">
      <NSkel w={36} h={36} rounded />
      <div>
        <NSkel w={120} h={14} />
        <NSkel w={70} h={11} style={{ marginTop: 6 }} />
      </div>
    </div>
    <NSkel w={22} h={22} rounded />
  </div>
);

const NumberCard = ({ num, selected, onToggle, badge }) => (
  <div
    onClick={() => onToggle(num)}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "9px 12px",
      borderRadius: 8,
      border: selected ? "2px solid #556ee6" : "1.5px solid #e9ecef",
      background: selected ? "#f4f5ff" : "#fff",
      cursor: "pointer",
      transition: "all 0.18s",
      marginBottom: 5,
    }}
  >
    <div className="d-flex align-items-center gap-2">
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: selected ? "#556ee6" : "#f0f2f7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "background 0.18s",
        }}
      >
        <i className="bx bx-phone" style={{ fontSize: 18, color: selected ? "#fff" : "#9199a9" }} />
      </div>
      <div>
        <div style={{ fontWeight: 600, fontSize: 15, color: "#343a40", letterSpacing: 0.3 }}>
          {num.phone_number}
        </div>
        {num.locality && (
          <div style={{ fontSize: 12, color: "#9199a9" }}>
            {num.locality}{num.region ? `, ${num.region}` : ""}
          </div>
        )}
      </div>
    </div>
    <div className="d-flex align-items-center gap-2">
      {badge}
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          border: selected ? "2px solid #556ee6" : "2px solid #ced4da",
          background: selected ? "#556ee6" : "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "all 0.18s",
        }}
      >
        {selected && <i className="bx bx-check" style={{ fontSize: 14, color: "#fff" }} />}
      </div>
    </div>
  </div>
);

const DEFAULT_COUNTRIES = [{ code: "US", label: "🇺🇸 United States" }];

const NumberSearchPanel = ({
  countries = DEFAULT_COUNTRIES,
  country,
  onCountryChange,
  areaCode,
  onAreaCodeChange,
  autoSuggest,
  onAutoSuggestChange,
  loading,
  results,
  isSelected,
  onToggle,
  onSearch,
  renderBadge, // optional (num) => node, shown on each card (e.g. FREE / price)
}) => {
  // Auto-suggest: search immediately when toggled on
  useEffect(() => {
    if (autoSuggest) onSearch();
    // eslint-disable-next-line
  }, [autoSuggest]);

  return (
    <div>
      <style>{`@keyframes np-shimmer {0%{background-position:-600px 0}100%{background-position:600px 0}}`}</style>

      {/* Country */}
      <div className="mb-3">
        <label className="form-label fw-semibold mb-1">Country</label>
        <select
          className="form-select"
          style={{ borderRadius: 10, border: "1.5px solid #e9ecef" }}
          value={country}
          onChange={(e) => onCountryChange(e.target.value)}
        >
          {countries.map((c) => (
            <option key={c.code} value={c.code}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Auto-suggest toggle */}
      <div
        className="d-flex align-items-center justify-content-between p-3 mb-3"
        style={{ background: "#f8f9ff", borderRadius: 10, border: "1.5px solid #e0e4ff" }}
      >
        <label htmlFor="np-autoSuggest" style={{ cursor: "pointer", marginBottom: 0 }}>
          <div className="fw-semibold" style={{ fontSize: 14, color: "#343a40" }}>
            Auto-suggest available numbers
          </div>
          <div style={{ fontSize: 12, color: "#9199a9", marginTop: 2 }}>
            Skip area code search and see numbers immediately
          </div>
        </label>
        <div className="form-check form-switch mb-0 ms-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="np-autoSuggest"
            checked={autoSuggest}
            onChange={(e) => onAutoSuggestChange(e.target.checked)}
            style={{ width: 42, height: 22, cursor: "pointer" }}
          />
        </div>
      </div>

      {/* Area code search */}
      <div className="mb-3">
        <label className="form-label fw-semibold">Area Code</label>
        <div className="input-group" style={{ borderRadius: 10, overflow: "hidden" }}>
          <span className="input-group-text" style={{ background: "#f8f9ff", border: "1.5px solid #e9ecef", borderRight: 0 }}>
            <i className="bx bx-hash text-primary" />
          </span>
          <input
            type="number"
            className="form-control"
            style={{ border: "1.5px solid #e9ecef", borderLeft: 0, borderRight: 0 }}
            value={areaCode}
            onChange={(e) => onAreaCodeChange(e.target.value)}
            placeholder="e.g. 415"
            disabled={autoSuggest}
            onKeyDown={(e) => e.key === "Enter" && onSearch()}
          />
          <button
            className="btn btn-primary px-4"
            type="button"
            onClick={onSearch}
            disabled={loading}
            style={{ borderRadius: "0 10px 10px 0", fontWeight: 600 }}
          >
            {loading ? <span className="spinner-border spinner-border-sm" /> : <><i className="bx bx-search me-1" />Search</>}
          </button>
        </div>
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div style={{ borderRadius: 10, border: "1.5px solid #ededf0", padding: 6 }}>
          {[1, 2, 3, 4, 5].map((i) => <NumberCardSkeleton key={i} />)}
        </div>
      )}

      {/* Empty state */}
      {!loading && results && results.length === 0 && (
        <div className="text-center py-4" style={{ background: "#fff8f0", borderRadius: 12, border: "1.5px solid #ffe4c4" }}>
          <i className="bx bx-phone-off" style={{ fontSize: 36, color: "#f5a623" }} />
          <p className="fw-semibold mb-1 mt-2">No numbers found</p>
          <p className="text-muted mb-2" style={{ fontSize: 13 }}>
            Try a different area code or clear it to search all.
          </p>
          <button className="btn btn-sm btn-outline-primary" onClick={() => { onAreaCodeChange(""); onSearch(); }}>
            Search all numbers
          </button>
        </div>
      )}

      {/* Number list */}
      {!loading && results && results.length > 0 && (
        <>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <label className="form-label fw-semibold mb-0" style={{ fontSize: 13 }}>Select a Number</label>
            <span className="badge" style={{ background: "#eef0fb", color: "#556ee6", fontWeight: 600, fontSize: 12 }}>
              {results.length} available
            </span>
          </div>
          <div style={{ maxHeight: 260, overflowY: "auto", borderRadius: 10, border: "1.5px solid #e9ecef", padding: 6 }}>
            {results.map((num) => (
              <NumberCard
                key={num.phone_number}
                num={num}
                selected={isSelected(num)}
                onToggle={onToggle}
                badge={renderBadge ? renderBadge(num) : null}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default NumberSearchPanel;
